/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { AxiosError } from 'axios';
import { throwGlobalError } from '~adapters/helpers/error';
import { SDLC_DATA_API_BASE_PATH } from '~adapters/helpers/sdlc-data';
import { API_V2_BASE_URL } from '~adapters/helpers/urls';
import { axiosClient, axiosToCatch } from '~shared/helpers/axios-clients';
import { HttpStatus } from '~shared/types/request';
import { mapToLinkedSonarIssue } from '../helpers';
import {
  AtlassianApplicationConfigurationPayload,
  AtlassianApplicationConfigurationResponse,
  JiraBulkWorkItemCreatePayload,
  JiraBulkWorkItemCreatePayloadMode,
  JiraBulkWorkItemQueryResult,
  JiraBulkWorkItemsQueryParams,
  JiraOrganizationBinding,
  JiraOrganizationBindingPatchPayload,
  JiraOrganizationBindingPostPayload,
  JiraOrganizationBindingPostResponse,
  JiraOrganizationPropertiesPatchPayload,
  JiraPermission,
  JiraProject,
  JiraProjectBinding,
  JiraProjectSummary,
  JiraSelectedWorkTypes,
  JiraWorkItem,
  JiraWorkItemPayload,
  JiraWorkItemQueryParams,
  JiraWorkItemResourceType,
  JiraWorkItemUnlinkQueryParams,
  JiraWorkType,
  JiraWorkTypeQueryParams,
  LinkedResourcesCount,
} from '../types';

const JIRA_BINDING_BASE_PATH = `${SDLC_DATA_API_BASE_PATH}/jira`;
const ATLASSIAN_BASE_PATH = `${SDLC_DATA_API_BASE_PATH}/atlassian`;

/**
 * Organization Jira binding
 */
export const JIRA_ORGANIZATION_BINDINGS_PATH = `${JIRA_BINDING_BASE_PATH}/organization-bindings`;
export const JIRA_ORGANIZATION_EDIT_BINDING_PATH = `${JIRA_BINDING_BASE_PATH}/organization-binding-edit`;

export function getJiraOrganizationBinding(
  sonarOrganizationUuid: string,
): Promise<JiraOrganizationBinding | null> {
  return axiosClient
    .get<JiraOrganizationBinding>(`${API_V2_BASE_URL}${JIRA_ORGANIZATION_BINDINGS_PATH}`, {
      params: { sonarOrganizationUuid },
    })
    .then((response) => {
      if (response.id === '') {
        return null;
      }

      return response;
    });
}

export function postJiraBinding(
  data: JiraOrganizationBindingPostPayload,
): Promise<JiraOrganizationBindingPostResponse> {
  return axiosClient.post<JiraOrganizationBindingPostResponse>(
    `${API_V2_BASE_URL}${JIRA_ORGANIZATION_BINDINGS_PATH}`,
    data,
  );
}

export function patchJiraBinding(
  data: JiraOrganizationBindingPatchPayload,
): Promise<JiraOrganizationBinding> {
  return axiosClient.patch<JiraOrganizationBinding>(
    `${API_V2_BASE_URL}${JIRA_ORGANIZATION_BINDINGS_PATH}`,
    data,
  );
}

export function patchJiraOrgBindingProperties(
  data: JiraOrganizationPropertiesPatchPayload,
): Promise<JiraOrganizationBinding> {
  return axiosClient.patch<JiraOrganizationBinding>(
    `${API_V2_BASE_URL}${JIRA_ORGANIZATION_EDIT_BINDING_PATH}`,
    data,
  );
}

export function deleteJiraBinding(sonarOrganizationUuid: string): Promise<void> {
  return axiosClient.delete(`${API_V2_BASE_URL}${JIRA_ORGANIZATION_BINDINGS_PATH}`, {
    params: { sonarOrganizationUuid },
  });
}

export const ATLASSIAN_AUTH_URL_PATH = `${ATLASSIAN_BASE_PATH}/auth-url`;

export function getAtlassianAuthUrl(
  sonarOrganizationKey: string,
  sonarOrganizationUuid: string,
): Promise<string | null> {
  return axiosClient.get<string>(`${API_V2_BASE_URL}${ATLASSIAN_AUTH_URL_PATH}`, {
    params: { sonarOrganizationKey, sonarOrganizationUuid },
  });
}

export const ATLASSIAN_APPLICATION_CONFIGURATION_PATH = `${ATLASSIAN_BASE_PATH}/application-configuration`;

export function getAtlassianApplicationConfiguration() {
  return axiosClient
    .get<AtlassianApplicationConfigurationResponse>(
      `${API_V2_BASE_URL}${ATLASSIAN_APPLICATION_CONFIGURATION_PATH}`,
      {
        validateStatus: (status) =>
          (status >= HttpStatus.Ok && status < HttpStatus.MultipleChoices) ||
          status === HttpStatus.NotFound, // So that the 404 is not caught by the global error handler which would display a toast to the user
      },
    )
    .then((response) => {
      // In case of a 404, the response won't include the clientId field
      return response.clientId ? response : null;
    });
}

export function postAtlassianApplicationConfiguration(
  data: AtlassianApplicationConfigurationPayload,
): Promise<AtlassianApplicationConfigurationResponse> {
  return axiosClient.post<AtlassianApplicationConfigurationResponse>(
    `${API_V2_BASE_URL}${ATLASSIAN_APPLICATION_CONFIGURATION_PATH}`,
    data,
  );
}

/**
 * Project Jira binding
 */

export const JIRA_PROJECT_BINDINGS_PATH = `${JIRA_BINDING_BASE_PATH}/project-bindings`;

export function getJiraProjectBinding(sonarProjectId: string) {
  // When the project is not bound, the backend returns an object whose field values are empty
  return axiosClient.get<JiraProjectBinding>(`${API_V2_BASE_URL}${JIRA_PROJECT_BINDINGS_PATH}`, {
    params: { sonarProjectId },
  });
}

/**
 * Create a new Jira project binding.
 */
export function createJiraProjectBinding(
  data: Omit<JiraProjectBinding, 'id'>,
): Promise<JiraProjectBinding> {
  return axiosClient.post<JiraProjectBinding>(
    `${API_V2_BASE_URL}${JIRA_PROJECT_BINDINGS_PATH}`,
    data,
  );
}

export function updateJiraProjectBinding(
  data: Pick<JiraProjectBinding, 'sonarProjectId' | 'jiraProjectKey'>,
): Promise<JiraProjectBinding> {
  return axiosClient.patch<JiraProjectBinding>(
    `${API_V2_BASE_URL}${JIRA_PROJECT_BINDINGS_PATH}`,
    data,
  );
}

/**
 * Delete a Jira project binding
 */
export function deleteJiraProjectBinding(sonarProjectId: string): Promise<void> {
  return axiosClient.delete(`${API_V2_BASE_URL}${JIRA_PROJECT_BINDINGS_PATH}`, {
    params: { sonarProjectId },
  });
}

/**
 * Jira projects listing
 */

export const JIRA_PROJECTS_PATH = `${JIRA_BINDING_BASE_PATH}/projects`;

export function getJiraProjects(sonarOrganizationUuid: string): Promise<JiraProject[]> {
  return axiosClient
    .get<JiraProject[]>(`${API_V2_BASE_URL}${JIRA_PROJECTS_PATH}`, {
      params: { sonarOrganizationUuid },
    })
    .catch((e: AxiosError) => {
      if (
        e.response?.status &&
        e.response.status >= HttpStatus.BadRequest &&
        e.response.status < HttpStatus.InternalServerError
      ) {
        return [];
      }
      return throwGlobalError(e);
    });
}

/**
 * Jira work item
 */

export const JIRA_WORK_ITEMS_PATH = `${JIRA_BINDING_BASE_PATH}/work-items`;

export function createJiraWorkItem(data: JiraWorkItemPayload): Promise<JiraWorkItem> {
  return axiosClient.post<JiraWorkItem>(`${API_V2_BASE_URL}${JIRA_WORK_ITEMS_PATH}`, data);
}

export function getJiraWorkItem(
  sonarProjectId: string,
  resourceId: string,
  resourceType: JiraWorkItemResourceType,
): Promise<JiraWorkItem | null> {
  const params: JiraWorkItemQueryParams = { sonarProjectId, resourceId, resourceType };
  return axiosToCatch
    .get<JiraWorkItem>(`${API_V2_BASE_URL}${JIRA_WORK_ITEMS_PATH}`, { params })
    .catch((e: AxiosError) => {
      if (e.status === HttpStatus.NotFound) {
        // 404 is expected when the issue has no work item
        return null;
      }
      return throwGlobalError(e);
    });
}

export function unlinkSonarIssueFromJiraWorkItem(
  sonarProjectId: string,
  resourceId: string,
  resourceType: JiraWorkItemResourceType,
): Promise<void> {
  const params: JiraWorkItemUnlinkQueryParams = { sonarProjectId, resourceId, resourceType };
  return axiosClient.delete(`${API_V2_BASE_URL}${JIRA_WORK_ITEMS_PATH}`, { params });
}

/**
 * Jira bulk work items
 */
export const JIRA_BULK_WORK_ITEMS_PATH_NEW = `${JIRA_BINDING_BASE_PATH}/bulk-work-items`;
// To be removed in scope of SC-30100
export const JIRA_BULK_WORK_ITEMS_PATH = `${JIRA_BINDING_BASE_PATH}/bulk/work-items`;

export function getJiraBulkWorkItems(
  params: JiraBulkWorkItemsQueryParams,
): Promise<JiraBulkWorkItemQueryResult[]> {
  return axiosClient.get<JiraBulkWorkItemQueryResult[]>(
    `${API_V2_BASE_URL}${JIRA_BULK_WORK_ITEMS_PATH}`,
    {
      params: {
        sonarIssueKeys: params.sonarIssueKeys.join(','),
        sonarProjectId: params.sonarProjectId,
      },
    },
  );
}

export async function createBulkWorkItems(data: JiraBulkWorkItemCreatePayload) {
  const result = await (data.mode === JiraBulkWorkItemCreatePayloadMode.ManyToMany
    ? axiosToCatch
        .post<{
          linkedIssues: JiraBulkWorkItemQueryResult[];
        }>(`${API_V2_BASE_URL}${JIRA_BULK_WORK_ITEMS_PATH_NEW}`, data)
        .then((result) => result.linkedIssues)
    : axiosToCatch.post<JiraBulkWorkItemQueryResult[]>(
        `${API_V2_BASE_URL}${JIRA_BULK_WORK_ITEMS_PATH}/create`,
        data,
      ));

  return mapToLinkedSonarIssue(result);
}

export function getJiraProcessingWorkItems(): Promise<Record<string, boolean>> {
  return Promise.resolve({});

  // Uncomment the following code when the endpoint is implemented
  // return axiosClient
  //   .get<{ sonarIssues: Array<string> }>(`${SDLC_DATA_BASE_PATH}/jira/processing-work-items`)
  //   .then(({ sonarIssues }) => Object.fromEntries(sonarIssues.map((issue) => [issue, true])))
}

/**
 * WORK TYPES
 */
export const JIRA_WORK_TYPES_PATH = `${JIRA_BINDING_BASE_PATH}/work-types`;

export function getJiraWorkTypes(params: JiraWorkTypeQueryParams): Promise<JiraWorkType[]> {
  return axiosClient.get<JiraWorkType[]>(`${API_V2_BASE_URL}${JIRA_WORK_TYPES_PATH}`, { params });
}

/**
 * Patch the selected work types to the server
 */
export function patchJiraSelectedWorkTypes(data: JiraSelectedWorkTypes): Promise<void> {
  return axiosClient.patch(`${API_V2_BASE_URL}${JIRA_WORK_TYPES_PATH}`, data);
}

/**
 * PROJECT SUMMARY
 */

export const JIRA_PROJECT_SUMMARY_PATH = `${JIRA_BINDING_BASE_PATH}/earliest-unreleased-versions`;

export function getJiraProjectSummary(sonarProjectId: string): Promise<JiraProjectSummary | null> {
  const path = `${API_V2_BASE_URL}${JIRA_PROJECT_SUMMARY_PATH}/${encodeURIComponent(sonarProjectId)}`;
  return axiosClient
    .get<JiraProjectSummary>(path, {
      validateStatus: (status) =>
        (status >= HttpStatus.Ok && status < HttpStatus.MultipleChoices) ||
        status === HttpStatus.NotFound, // So that the 404 is not caught by the global error handler which would display a toast to the user
    })
    .then((response) => {
      // In case of a 404, the response won't include the name field
      return response.name ? response : null;
    });
}

/**
 * USER ACTIONS
 */

export const JIRA_USER_ACTIONS_PATH = `${JIRA_BINDING_BASE_PATH}/user-actions`;

export function getJiraUserActions(sonarProjectId: string): Promise<JiraPermission[]> {
  return axiosClient.get<JiraPermission[]>(`${API_V2_BASE_URL}${JIRA_USER_ACTIONS_PATH}`, {
    params: { sonarProjectId },
  });
}

/**
 * LINKED ISSUES COUNT
 */

export const JIRA_LINKED_ISSUES_COUNT_PATH = `${JIRA_BINDING_BASE_PATH}/linked-issues-count`;

export function getJiraLinkedIssuesCount(
  sonarProjectId: string,
): Promise<LinkedResourcesCount | null> {
  const path = `${API_V2_BASE_URL}${JIRA_LINKED_ISSUES_COUNT_PATH}/${encodeURIComponent(sonarProjectId)}`;
  return axiosClient
    .get<LinkedResourcesCount>(path, {
      validateStatus: (status) =>
        (status >= HttpStatus.Ok && status < HttpStatus.MultipleChoices) ||
        status === HttpStatus.NotFound, // So that the 404 is not caught by the global error handler which would display a toast to the user
    })
    .then((response) => {
      // In case of a 404, the response won't include the name field
      return response.count ? response : null;
    });
}

/**
 * Helper function to create hyperlinks in the format [linkText|url].
 */
export function createHyperlink(linkText: string, url: string): string {
  return `[${linkText}|${url}]`;
}
